<!-- DescriptionModal.svelte -->
<script lang="ts">
	import Modal from './Modal.svelte'
	import modal, { type ExtraTypeMap } from '$lib/stores/modal-state'
	import { urlState } from '$lib/stores/url-state'
	import { track } from '$lib/utils/track'
	import DescriptionForm from './DescriptionForm.svelte'
	import Button from '$lib/components/buttons/Button.svelte'
	import type { MutateTripContext } from '$lib/context/async-operations'
	import { getContext } from 'svelte'
	import { getContextClient, queryStore } from '@urql/svelte'
	import {
		TripBaseDocument,
		type TripBaseQuery,
		type TripBaseQueryVariables,
	} from '$lib/queries/generated/QueryTrip'
	import { browser } from '$app/environment'

	let { blockId, tripId, itineraryId } = $derived($urlState)

	const mutateTrip = getContext<MutateTripContext>('mutateTrip')

	const client = getContextClient()

	let tripStore = $derived(
		queryStore<TripBaseQuery, TripBaseQueryVariables>({
			client,
			query: TripBaseDocument,
			variables: { tripId },
			requestPolicy: 'cache-first',
			pause: !browser || !tripId || !tripId?.startsWith('trp_'),
		}),
	)

	let defaultRootBlockId = $derived(
		$tripStore?.data?.trip?.rootBlocks?.find((b) => b.position === 0)?.id ?? null,
	)

	let isSaving = $state(false)
	let error = $state('')

	const blockData = modal?.extra<ExtraTypeMap, 'edit-description'>('edit-description')
	let description = $derived(blockData?.description ?? 'No description provided')

	let currentContent = $derived(
		description !== null && description !== '' ? description : 'No description provided',
	)
	let hasDescriptionError = $state(false)

	// Since currentContent is now $derived, we need a mutable state to track user edits
	let userEditedContent = $state('')

	$effect(() => {
		let cancelled = false

		// Initialize userEditedContent with the derived value
		if (userEditedContent === '' && !cancelled) {
			userEditedContent = currentContent
		}

		return () => {
			cancelled = true
		}
	})

	function handleChange(content: string, error: boolean) {
		userEditedContent = content
		hasDescriptionError = error
	}

	async function handleSave() {
		if (isSaving) return
		isSaving = true

		try {
			if (blockId === null && (itineraryId === null || itineraryId === defaultRootBlockId)) {
				track('Update Trip Description', { tripId })
				mutateTrip.updateTripDescription(tripId, userEditedContent)
			} else if (blockId !== null) {
				track('Update Block Description', { blockId })
				mutateTrip.updateBlockDescription(blockId, userEditedContent)
			} else if (itineraryId !== null) {
				track('Update Itinerary Description', { itineraryId })
				mutateTrip.updateBlockDescription(itineraryId, userEditedContent)
			}
			isSaving = false
			modal.close()
		} catch (err) {
			error = 'Failed to save changes. Please try again.'
		}
	}

	// Check if the content has changed
	let hasContentChanged = $derived(userEditedContent !== currentContent)
</script>

<Modal open={true} isLightOverride={false} size="sm:max-w-xl" title="Edit Description">
	{#if currentContent}
		<DescriptionForm initialContent={currentContent} onchange={handleChange} />
	{/if}

	{#snippet actions()}
		<Button
			disabled={isSaving || hasDescriptionError || !hasContentChanged}
			loading={isSaving}
			onclick={handleSave}
			theme="brand"
			size="sm">Save</Button
		>
	{/snippet}
</Modal>
